import { useId, useState } from 'react';
import type { LatencyRow, ProviderItem, StateResponse } from '../lib/api.js';
import type { DashboardResource } from '../lib/dashboardResource.js';
import { providerDisplayName } from '../lib/providerDisplayName.js';
import { getProviderSeverityTone, resolveProviderState } from '../lib/providerState.js';
import { statusPageUrl } from '../lib/statusPageUrl.js';
import ExternalIndicator from './ExternalIndicator.js';
import ProviderInfoModal from './ProviderInfoModal.js';
import CommandPanel from './ui/CommandPanel.js';
import IconButton from './ui/IconButton.js';
import { usePersistedPanelState } from '../hooks/usePersistedPanelState.js';
import SectionHeader from './ui/SectionHeader.js';
import StatusGlow from './ui/StatusGlow.js';

// TierStrip v0.6.2 — collapsible by default with persisted visitor state.
// Keeps Tier-0 card actions while removing mini latency sparklines now that
// latency/history has a dedicated page.
interface Props {
  providers: ProviderItem[];
  state: StateResponse['providers'];
  latency?: DashboardResource<LatencyRow[]>;
}

export default function TierStrip({ providers, state, latency }: Props) {
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [expanded, setExpanded] = usePersistedPanelState('panel:tier-0', true);
  const contentId = useId();
  const active = providers.find((p) => p.slug === activeSlug) ?? null;
  const getState = (p: ProviderItem) => resolveProviderState(state, p);
  const sevLabel: Record<number, string> = {
    0: 'OK',
    1: 'DEG',
    2: 'MAJ',
    3: 'DOWN',
  };
  void latency;

  return (
    <CommandPanel>
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <span className="text-accent">◉</span>
            <span>TIER 0 · CRITICAL INFRASTRUCTURE</span>
          </span>
        }
        right={<span className="text-white normal-case tracking-normal">{providers.length} providers</span>}
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        contentId={contentId}
      />
      {expanded && (
        <>
          {/* 5 columns at xl, stepping down to 4/3/2 on smaller breakpoints. At
              375px (mobile) we stay at grid-cols-2; min-w-0 + truncate keeps
              long names contained. */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-2 p-2">
            {providers.map((p) => {
              const msg = getState(p)?.message;
              const tone = getProviderSeverityTone(state, p);
              const displayName = providerDisplayName(p.slug, p.name);

              return (
                <article
                  key={p.slug}
                  title={msg ? `${p.name} — ${msg}` : p.name}
                  className="border border-border rounded-md px-3 py-3 hover:border-accent hover:bg-surface2 text-sm font-mono flex flex-col gap-3 min-w-0 relative min-h-[104px]"
                >
                  <div className="flex items-start gap-2 min-w-0 pr-10">
                    <span className="text-white leading-tight truncate min-w-0 font-semibold">
                      {displayName}
                    </span>
                    <IconButton
                      label={`${p.name} details`}
                      onClick={() => setActiveSlug(p.slug)}
                      stopPropagation
                      className="w-6 h-6 rounded-full border border-warn/70 bg-warn/15 text-warn hover:bg-warn hover:text-bg text-xs leading-none font-bold shrink-0"
                    >
                      ?
                    </IconButton>
                  </div>
                  <div className="flex items-center justify-between gap-2 min-w-0 pr-9">
                    {p.source_type === 'external' ? (
                      <ExternalIndicator href={statusPageUrl(p)} label={p.name} />
                    ) : (
                      <StatusGlow severity={tone}>{sevLabel[tone]}</StatusGlow>
                    )}
                    <IconButton
                      as="link"
                      href={statusPageUrl(p)}
                      label={`Open ${p.name} status page`}
                      stopPropagation
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute top-2 right-2 w-8 h-8 rounded-md border border-accent/70 bg-accent/15 text-accent hover:bg-accent hover:text-bg text-base font-bold leading-none"
                    >
                      ↗
                    </IconButton>
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}
      {active && (
        <ProviderInfoModal
          open
          onClose={() => setActiveSlug(null)}
          name={active.name}
          description={active.description ?? ''}
          servicesCovered={active.services_covered}
          statusUrl={statusPageUrl(active)}
        />
      )}
    </CommandPanel>
  );
}
